import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import Card from '../../components/Common/Card';
import Select from '../../components/Common/Select';
import Button from '../../components/Common/Button';
import PageWrapper from '../../components/Common/PageWrapper';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Download, FileText, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

const AnalyticsDashboard = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [colleges, setColleges] = useState([]);
  const [selectedCollege, setSelectedCollege] = useState('');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState('month');
  const [expandedSection, setExpandedSection] = useState('all');

  useEffect(() => {
    fetchColleges();
  }, []);

  useEffect(() => {
    if (selectedCollege) {
      fetchAnalytics();
    }
  }, [selectedCollege, dateRange]);

  const fetchColleges = async () => {
    try {
      const { data } = await api.get('/colleges', { params: { page: 1, limit: 1000 } });
      if (data.success) {
        setColleges(data.data.map(c => ({ value: c._id, label: c.collegeName })));
      }
    } catch (error) {
      toast.error('Failed to fetch institutes');
    }
  };

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/analytics/college/${selectedCollege}`, {
        params: { timeframe: dateRange }
      });
      if (data.success) {
        setAnalyticsData(data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    if (expandedSection === section) {
      setExpandedSection('all');
    } else {
      setExpandedSection(section);
    }
  };

  const isSectionExpanded = (section) => {
    return expandedSection === 'all' || expandedSection === section;
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(18);
    const collegeName = colleges.find(c => c.value === selectedCollege)?.label || 'All Institutes';
    doc.text(`Analytics Report - ${collegeName}`, 14, 22);
    
    doc.setFontSize(12);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);
    
    // Add KPIs
    doc.setFontSize(14);
    doc.text('Key Performance Indicators', 14, 40);
    
    const kpiData = [
      ['Metric', 'Value'],
      ['Course Completion Rate', `${analyticsData?.kpis?.courseCompletion || 0}%`],
      ['Wellbeing Index', analyticsData?.kpis?.wellbeingIndex || 0],
      ['Cognitive Index', analyticsData?.kpis?.cognitiveIndex || 0],
      ['Total Students', analyticsData?.students?.total || 0],
      ['Active Students', analyticsData?.students?.active || 0],
      ['Open Escalations', analyticsData?.escalations?.open || 0]
    ];
    
    doc.autoTable({
      startY: 45,
      head: [kpiData[0]],
      body: kpiData.slice(1),
      theme: 'striped',
      headStyles: { fillColor: [41, 128, 185] }
    });
    
    // Save the PDF
    doc.save(`analytics_report_${collegeName.replace(/\s+/g, '_')}.pdf`);
  };

  const exportToExcel = () => {
    const collegeName = colleges.find(c => c.value === selectedCollege)?.label || 'All Institutes';
    
    // Prepare data
    const kpiData = [
      ['Metric', 'Value'],
      ['Course Completion Rate', `${analyticsData?.kpis?.courseCompletion || 0}%`],
      ['Wellbeing Index', analyticsData?.kpis?.wellbeingIndex || 0],
      ['Cognitive Index', analyticsData?.kpis?.cognitiveIndex || 0],
      ['Total Students', analyticsData?.students?.total || 0],
      ['Active Students', analyticsData?.students?.active || 0],
      ['Open Escalations', analyticsData?.escalations?.open || 0]
    ];
    
    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(kpiData);
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Analytics');
    
    // Save workbook
    XLSX.writeFile(wb, `analytics_report_${collegeName.replace(/\s+/g, '_')}.xlsx`);
  };

  const colorsList = isDark 
    ? ['#3B82F6', '#34D399', '#FBBF24', '#F87171', '#A78BFA', '#F472B6', '#22D3EE']
    : ['#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];

  const domainData = analyticsData?.coachSessions?.domainWise?.map(item => ({
    name: item._id.replace(/-/g, ' '),
    value: item.count
  })) || [];

  const escalationData = analyticsData?.escalations?.byPriority?.map(item => ({
    name: item._id,
    value: item.count
  })) || [];

  const categoryData = analyticsData?.escalations?.byCategory?.map(item => ({
    name: item._id.replace(/-/g, ' '),
    value: item.count
  })) || [];

  const kpiData = analyticsData?.kpis ? [
    { name: 'Course Completion', value: analyticsData.kpis.courseCompletion },
    { name: 'Wellbeing Index', value: analyticsData.kpis.wellbeingIndex },
    { name: 'Cognitive Index', value: analyticsData.kpis.cognitiveIndex },
  ] : [];

  const monthlyProgressData = analyticsData?.engagement?.monthlyProgress || [
    { month: 'Jan', completion: 65, wellbeing: 72, cognitive: 68 },
    { month: 'Feb', completion: 70, wellbeing: 75, cognitive: 70 },
    { month: 'Mar', completion: 75, wellbeing: 78, cognitive: 73 },
    { month: 'Apr', completion: 80, wellbeing: 80, cognitive: 75 },
    { month: 'May', completion: 78, wellbeing: 76, cognitive: 72 },
    { month: 'Jun', completion: 85, wellbeing: 82, cognitive: 78 },
  ];

  const quotientRadarData = analyticsData?.engagement?.radarData || [
    { subject: 'Cognitive', A: 75, fullMark: 100 },
    { subject: 'Social', A: 68, fullMark: 100 },
    { subject: 'Leadership', A: 72, fullMark: 100 },
    { subject: 'Self', A: 80, fullMark: 100 },
    { subject: 'Physical', A: 65, fullMark: 100 },
    { subject: 'Digital', A: 85, fullMark: 100 },
    { subject: 'Service', A: 70, fullMark: 100 },
  ];

  const sessionRadarData = analyticsData?.engagement?.sessionRadar || [
    { subject: 'Academic', A: 82, fullMark: 100 },
    { subject: 'Mental Health', A: 78, fullMark: 100 },
    { subject: 'Career', A: 85, fullMark: 100 },
    { subject: 'Personal', A: 75, fullMark: 100 },
    { subject: 'Other', A: 70, fullMark: 100 },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <PageWrapper
      title="Advanced Analytics Dashboard"
      subtitle="Live insights across student progress, wellbeing, and escalations"
      actions={
        <div className="flex flex-wrap items-center gap-3">
          <Select
            value={selectedCollege}
            onChange={(e) => setSelectedCollege(e.target.value)}
            options={[{ value: '', label: 'All Institutes' }, ...colleges]}
            className="w-56"
          />
          <Select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            options={[
              { value: 'week', label: 'Last Week' },
              { value: 'month', label: 'Last Month' },
              { value: 'quarter', label: 'Last Quarter' },
              { value: 'year', label: 'Last Year' },
              { value: 'all', label: 'All Time' }
            ]}
            className="w-40"
          />
          <div className="flex items-center gap-2">
            <Button icon={FileText} variant="outline" onClick={exportToExcel}>
              Excel
            </Button>
            <Button icon={Download} variant="primary" onClick={exportToPDF}>
              PDF
            </Button>
          </div>
        </div>
      }
    >

      {!selectedCollege ? (
        <Card>
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-300 mb-4">Please select an institute to view analytics</p>
            <Select
              value={selectedCollege}
              onChange={(e) => setSelectedCollege(e.target.value)}
              options={colleges}
              className="max-w-md mx-auto"
            />
          </div>
        </Card>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-300">Total Students</p>
                <p className={`text-3xl font-bold mt-2 ${isDark ? 'text-blue-400' : 'text-primary-600'}`}>
                  {analyticsData?.students?.total || 0}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {analyticsData?.students?.growth > 0 ? '+' : ''}{analyticsData?.students?.growth || 0}% from last period
                </p>
              </div>
            </Card>
            <Card>
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-300">Active Students</p>
                <p className={`text-3xl font-bold mt-2 ${isDark ? 'text-emerald-400' : 'text-green-600'}`}>
                  {analyticsData?.students?.active || 0}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {((analyticsData?.students?.active / analyticsData?.students?.total) * 100 || 0).toFixed(1)}% engagement rate
                </p>
              </div>
            </Card>

            <Card>
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-300">Open Escalations</p>
                <p className={`text-3xl font-bold mt-2 ${isDark ? 'text-rose-400' : 'text-red-600'}`}>
                  {analyticsData?.escalations?.open || 0}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {analyticsData?.escalations?.critical || 0} critical cases
                </p>
              </div>
            </Card>
          </div>

          {/* Performance KPIs Section */}
          <Card>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Performance KPIs</h2>
              <button 
                onClick={() => toggleSection('kpis')}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {isSectionExpanded('kpis') ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
            </div>
            
            {isSectionExpanded('kpis') && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={kpiData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(40, 75, 179, 0.2)' : '#e2e8f0'} />
                      <XAxis dataKey="name" tick={{ fill: isDark ? '#cbd5e1' : '#475569', fontSize: 11 }} />
                      <YAxis tick={{ fill: isDark ? '#cbd5e1' : '#475569', fontSize: 11 }} />
                      <Tooltip contentStyle={{ backgroundColor: isDark ? '#0F225C' : '#ffffff', borderColor: isDark ? '#1a3884' : '#e2e8f0', borderRadius: '8px', color: isDark ? '#ffffff' : '#000000' }} />
                      <Bar dataKey="value" fill={isDark ? '#3B82F6' : '#0ea5e9'} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={monthlyProgressData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(40, 75, 179, 0.2)' : '#e2e8f0'} />
                      <XAxis dataKey="month" tick={{ fill: isDark ? '#cbd5e1' : '#475569', fontSize: 11 }} />
                      <YAxis tick={{ fill: isDark ? '#cbd5e1' : '#475569', fontSize: 11 }} />
                      <Tooltip contentStyle={{ backgroundColor: isDark ? '#0F225C' : '#ffffff', borderColor: isDark ? '#1a3884' : '#e2e8f0', borderRadius: '8px', color: isDark ? '#ffffff' : '#000000' }} />
                      <Legend wrapperStyle={{ fontSize: 11, color: isDark ? '#cbd5e1' : '#475569' }} />
                      <Line type="monotone" dataKey="completion" stroke={isDark ? '#3B82F6' : '#0ea5e9'} strokeWidth={2} dot={{ r: 4 }} />
                      <Line type="monotone" dataKey="wellbeing" stroke={isDark ? '#34D399' : '#10b981'} strokeWidth={2} dot={{ r: 4 }} />
                      <Line type="monotone" dataKey="cognitive" stroke={isDark ? '#FBBF24' : '#f59e0b'} strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </Card>



          {/* Escalations Section */}
          <Card>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Escalations Analysis</h2>
              <button 
                onClick={() => toggleSection('escalations')}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {isSectionExpanded('escalations') ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
            </div>
            
            {isSectionExpanded('escalations') && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-md font-medium mb-4 text-center text-gray-800 dark:text-gray-200">Escalations by Priority</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={escalationData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(40, 75, 179, 0.2)' : '#e2e8f0'} />
                      <XAxis dataKey="name" tick={{ fill: isDark ? '#cbd5e1' : '#475569', fontSize: 11 }} />
                      <YAxis tick={{ fill: isDark ? '#cbd5e1' : '#475569', fontSize: 11 }} />
                      <Tooltip contentStyle={{ backgroundColor: isDark ? '#0F225C' : '#ffffff', borderColor: isDark ? '#1a3884' : '#e2e8f0', borderRadius: '8px', color: isDark ? '#ffffff' : '#000000' }} />
                      <Bar dataKey="value" fill={isDark ? '#F87171' : '#ef4444'} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <h3 className="text-md font-medium mb-4 text-center text-gray-800 dark:text-gray-200">Escalations by Category</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={categoryData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {categoryData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={colorsList[index % colorsList.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: isDark ? '#0F225C' : '#ffffff', borderColor: isDark ? '#1a3884' : '#e2e8f0', borderRadius: '8px', color: isDark ? '#ffffff' : '#000000' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </Card>

          {/* Student Performance Section */}
          <Card>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Student Performance</h2>
              <button 
                onClick={() => toggleSection('students')}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {isSectionExpanded('students') ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
            </div>
            
            {isSectionExpanded('students') && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-md font-medium mb-4 text-center text-gray-800 dark:text-gray-200">Course Completion Trend</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={monthlyProgressData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(40, 75, 179, 0.2)' : '#e2e8f0'} />
                      <XAxis dataKey="month" tick={{ fill: isDark ? '#cbd5e1' : '#475569', fontSize: 11 }} />
                      <YAxis tick={{ fill: isDark ? '#cbd5e1' : '#475569', fontSize: 11 }} />
                      <Tooltip contentStyle={{ backgroundColor: isDark ? '#0F225C' : '#ffffff', borderColor: isDark ? '#1a3884' : '#e2e8f0', borderRadius: '8px', color: isDark ? '#ffffff' : '#000000' }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="completion" stroke={isDark ? '#34D399' : '#10b981'} strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <h3 className="text-md font-medium mb-4 text-center text-gray-800 dark:text-gray-200">Student Engagement by Module Type</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={[
                      { name: 'Videos', completion: 85 },
                      { name: 'Quizzes', completion: 78 },
                      { name: 'Reflections', completion: 65 },
                      { name: 'Handouts', completion: 50 },
                      { name: 'Assessments', completion: 72 },
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(40, 75, 179, 0.2)' : '#e2e8f0'} />
                      <XAxis dataKey="name" tick={{ fill: isDark ? '#cbd5e1' : '#475569', fontSize: 11 }} />
                      <YAxis tick={{ fill: isDark ? '#cbd5e1' : '#475569', fontSize: 11 }} />
                      <Tooltip contentStyle={{ backgroundColor: isDark ? '#0F225C' : '#ffffff', borderColor: isDark ? '#1a3884' : '#e2e8f0', borderRadius: '8px', color: isDark ? '#ffffff' : '#000000' }} />
                      <Bar dataKey="completion" fill={isDark ? '#A78BFA' : '#8b5cf6'} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </Card>
        </>
      )}
    </PageWrapper>
  );
};

export default AnalyticsDashboard;
